import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Cpu,
  AlertTriangle,
  Wrench,
  TrendingUp,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
  MessageSquare,
} from "lucide-react";
import { dashboardAPI } from "../api";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import clsx from "clsx";

const StatCard = ({
  title,
  value,
  change,
  changeType,
  icon: Icon,
  color,
  link,
}) => {
  const colorClasses = {
    blue: "from-blue-50 to-white border-blue-100",
    green: "from-emerald-50 to-white border-emerald-100",
    yellow: "from-amber-50 to-white border-amber-100",
    red: "from-rose-50 to-white border-rose-100",
  };

  const iconColors = {
    blue: "bg-blue-100 text-blue-600",
    green: "bg-emerald-100 text-emerald-600",
    yellow: "bg-amber-100 text-amber-600",
    red: "bg-rose-100 text-rose-600",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={clsx(
        "p-6 bg-gradient-to-br border rounded-2xl shadow-sm hover:shadow-md transition-shadow",
        colorClasses[color]
      )}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={clsx("p-3 rounded-xl", iconColors[color])}>
          <Icon className="w-6 h-6" />
        </div>
        {change && (
          <div
            className={clsx(
              "flex items-center gap-1 text-sm font-semibold",
              changeType === "increase" ? "text-emerald-600" : "text-rose-600"
            )}
          >
            {changeType === "increase" ? (
              <ArrowUpRight className="w-4 h-4" />
            ) : (
              <ArrowDownRight className="w-4 h-4" />
            )}
            {change}
          </div>
        )}
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900 mb-1">{value}</p>
        <p className="text-sm text-slate-600 font-medium">{title}</p>
      </div>
      {link && (
        <Link
          to={link}
          className="mt-4 flex items-center gap-1 text-sm text-slate-500 hover:text-blue-600 transition-colors"
        >
          View details <ChevronRight className="w-4 h-4" />
        </Link>
      )}
    </motion.div>
  );
};

const AlertItem = ({ alert }) => {
  const severityColors = {
    critical: "border-rose-200 bg-rose-50 text-rose-900",
    high: "border-amber-200 bg-amber-50 text-amber-900",
    medium: "border-blue-200 bg-blue-50 text-blue-900",
    low: "border-slate-200 bg-slate-50 text-slate-900",
  };

  const severityIcons = {
    critical: <XCircle className="w-5 h-5 text-rose-600" />,
    high: <AlertTriangle className="w-5 h-5 text-amber-600" />,
    medium: <AlertCircle className="w-5 h-5 text-blue-600" />,
    low: <AlertCircle className="w-5 h-5 text-slate-500" />,
  };

  return (
    <div
      className={clsx(
        "p-4 rounded-xl border shadow-sm",
        severityColors[alert.severity]
      )}
    >
      <div className="flex items-start gap-3">
        {severityIcons[alert.severity]}
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{alert.title}</p>
          <p className="text-sm opacity-80 mt-1">{alert.machine_name}</p>
        </div>
        <span className="text-xs opacity-60">
          {new Date(alert.created_at).toLocaleTimeString()}
        </span>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => dashboardAPI.getData().then((res) => res.data.data),
  });

  const { data: alertsData } = useQuery({
    queryKey: ["dashboard-alerts"],
    queryFn: () =>
      dashboardAPI.getAlerts({ limit: 5 }).then((res) => res.data.data),
  });

  const { data: statsData } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: () => dashboardAPI.getStats().then((res) => res.data.data),
  });

  if (isLoading)
    return (
      <div className="flex justify-center h-96 items-center">
        <Activity className="animate-spin text-blue-500" />
      </div>
    );

  const {
    machineStatus = {},
    alerts = {},
    maintenance = {},
  } = dashboardData || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Operations Dashboard
          </h1>
          <p className="text-slate-500">Real-time status of plant equipment</p>
        </div>
        <Link
          to="/assistant"
          className="btn btn-primary shadow-lg shadow-blue-500/20"
        >
          <MessageSquare className="w-4 h-4" /> Ask AI Assistant
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Machines"
          value={statsData?.totalMachines || machineStatus.total || 0}
          icon={Cpu}
          color="blue"
          link="/machines"
        />
        <StatCard
          title="Active Alerts"
          value={(Number(alerts.critical) || 0) +
            (Number(alerts.high) || 0) +
            (Number(alerts.medium) || 0)}
          change={Number(alerts.critical) > 0 ? `${alerts.critical} critical` : null}
          changeType="decrease"
          icon={AlertTriangle}
          color={Number(alerts.critical) > 0 ? "red" : "yellow"}
        />
        <StatCard
          title="Upcoming Maintenance"
          value={maintenance.upcoming || 0}
          icon={Wrench}
          color="green"
          link="/maintenance"
        />
        <StatCard
          title="In-Progress Maintenance"
          value={maintenance.inProgress || 0}
          icon={Wrench}
          color="yellow"
          link="/maintenance"
        />
        <StatCard
          title="Overdue Maintenance"
          value={maintenance.overdue || 0}
          icon={Wrench}
          color="red"
          link="/maintenance"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm"
        >
          <h2 className="text-lg font-bold text-slate-900 mb-6">
            Machine Health Distribution
          </h2>
          <div className="space-y-5">
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl">
              <span className="text-emerald-900 font-medium flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />{" "}
                Operational
              </span>
              <span className="text-xl font-bold text-emerald-900">
                {machineStatus.operational || 0}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl">
              <span className="text-amber-900 font-medium flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-500" /> In
                Maintenance
              </span>
              <span className="text-xl font-bold text-amber-900">
                {machineStatus.in_maintenance || 0}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-rose-50 rounded-xl">
              <span className="text-rose-900 font-medium flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-rose-500" /> Error /
                Offline
              </span>
              <span className="text-xl font-bold text-rose-900">
                {(Number(machineStatus.error) || 0) +
                  (Number(machineStatus.offline) || 0)}
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm lg:col-span-2"
        >
          <h2 className="text-lg font-bold text-slate-900 mb-4">
            Maintenance Activity
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={[
                  { name: "Mon", m: 4 },
                  { name: "Tue", m: 3 },
                  { name: "Wed", m: 5 },
                  { name: "Thu", m: 2 },
                  { name: "Fri", m: 6 },
                  { name: "Sat", m: 1 },
                  { name: "Sun", m: 2 },
                ]}
              >
                <defs>
                  <linearGradient id="colorM" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#64748b", fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="m"
                  stroke="#3b82f6"
                  fillOpacity={1}
                  fill="url(#colorM)"
                  name="Tasks"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
